import React, {Suspense, useEffect, useMemo} from 'react'
import styled, {ThemeProvider} from 'styled-components'
import {bearerHeader, TEXT_HTML} from './reducers'
import {getJWTPayload} from './lib/utils'
import {I18NProvider} from "./components/I18NProvider";
import {FormattedMessage} from "react-intl";
import {getInitLocale} from "./lib/getInitLocale";
import {Button, Loading, Notifications, PageLoader, Route, Router, useLocation} from "@startlibs/components";
import {IntlStartlibsProvider} from "./StartlibsConfig";
import {AppTheme} from "./AppTheme";
import {getFetcher, postFetcher} from "@startlibs/utils";
import {useToggle, Slot} from "@startlibs/core";
import {RegistrationForm} from "./pages/RegistrationForm";
import {LoginForm} from "./pages/LoginForm";
import {SsoLogin} from "./pages/SsoLogin";
import {UserNotFound} from "./errors/UserNotFound";
import {AlreadyActivated} from "./errors/AlreadyActivated";
import {UrlAccessDenied} from "./errors/UrlAccessDenied";
import {AccessDenied} from "./errors/AccessDenied";
import {DeletedAccountError} from "./errors/DeletedAccountError";
import {LinkExpiredError} from "./errors/LinkExpiredError";
import {RedefinePassword} from "./pages/RedefinePassword";
import {PasswordRecovery} from "./pages/PasswordRecovery";
import {Activate} from "./pages/Activate";
import {ActivationSuccess} from "./pages/ActivationSuccess";
import {EmailChangeFinish} from "./pages/EmailChangeFinish";
import {AccountSettings} from "./pages/AccountSettings";
import {Redirect, Switch} from "wouter";
import {JwtRegistrationForm} from "./pages/JwtRegistrationForm";
import _ from "lodash/fp";
// import {RegistrationForm} from "./pages/RegistrationForm";

export const LoadingUser = styled(({className}) => <div className={className}>
  <Loading size={40} borderWidth={6}/>
</div>)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  * {
    margin-left: auto;
    margin-right: auto;
  }
  img {
    max-width: 11rem;
    margin-bottom: 4rem;
  }
`

const PurviewPageLoader = styled(PageLoader)`
  background: #00D2FF;
`

const PAS_PAYLOAD_PARAM_NAME = 'pasAuthRequestPayload'
const PAS_SYSTEM_ID_PARAM = 'systemId'
const TOKEN_PARAM = 'token'
const AUTO_LOGIN = 'autoLogin'

export const App = () => {
  const state = useToggle({fetchingSystem:true})
  const userToggle = useToggle()
  const location = useLocation()

  const getNewRegistrationUsername = () => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('newRegistration') === 'true') {
      const payload = urlParams.get(PAS_PAYLOAD_PARAM_NAME)
      const username = urlParams.get('username')
      return getFetcher(`/pasapi/account/is-valid?${PAS_PAYLOAD_PARAM_NAME}=${payload}&username=${username}`)
        .then(({is_valid}) => is_valid !== "true" && username)
        .catch(() => false)
    } else {
      return Promise.resolve(false)
    }
  }

  const getAutoRegistration = () => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("registerToken")) {
      const login = JSON.parse(atob(urlParams.get("registerToken")))
      const locale = urlParams.get("locale")
      return postFetcher(`/pasapi/authenticate?locale=${locale}`,{...login,rememberMe:false,systemId:urlParams.get("systemId")})
        .then(({idToken,username,email,firstName,lastName}) => {
          if (username && idToken) {
            return {needsRegistration:idToken,newRegistration:true, registrationValues:{email,firstName,lastName,username}}
          } else {
            return {}
          }
        }).catch(() => {})
    } else if (urlParams.get("registerjwt")) {
      const jwt = urlParams.get("registerjwt")
      return fetch('/pasapi/validate-register-token',bearerHeader(jwt,TEXT_HTML))
        .then(({status}) => ({needsJwtRegistration:{jwt,validationFailed:status===401 || status === 403}}))
        .catch(() => ({needsJwtRegistration:{validationFailed:true,jwt}}))
    } else {
      return Promise.resolve({})
    }
  }

  const getSystem = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const payload = urlParams.get(PAS_PAYLOAD_PARAM_NAME)
    const token = urlParams.get(TOKEN_PARAM)
    const tokenPayload = getJWTPayload(token)
    const pathSystemId = window.location.pathname.indexOf("/auth")===0 ? window.location.pathname.replace(/\/auth\/?([^\/]*)\/?.*/,"$1") : ""
    const system = tokenPayload && tokenPayload.systemId ? tokenPayload.systemId : urlParams.get(PAS_SYSTEM_ID_PARAM)
    const autoLogin = urlParams.get(AUTO_LOGIN)
    const userEmail = urlParams.get('email') || ''
    const firstName = urlParams.get('firstname') || ''
    const lastName = urlParams.get('lastname') || ''

    const url =
      payload
        ? `/pasapi/system?${PAS_PAYLOAD_PARAM_NAME}=${payload}`
        : (system || pathSystemId)
          ?  `/pasapi/system/${system || pathSystemId}`
          : `/pasapi/systemref`
    urlParams.delete(TOKEN_PARAM)

    if (window.location.pathname.indexOf("/auth")!==0 && window.location.pathname.indexOf("/login")!==0) {
      window.history.replaceState('', null, window.location.pathname + '?' + urlParams.toString())
    }

    Promise.all([
      getNewRegistrationUsername(),
      getFetcher(url),
      getAutoRegistration()
    ])
      .then(([newRegistrationForUsername, {errors, ...system},login]) => {
        if (errors) {
          state.openWith({systemFetchError: errors, fetchingSystem: false})
        } else {
          if (urlParams.has(PAS_PAYLOAD_PARAM_NAME)) {
            urlParams.delete(PAS_PAYLOAD_PARAM_NAME)
            urlParams.set(PAS_SYSTEM_ID_PARAM,system.systemId)
            window.history.replaceState('', null, window.location.pathname + '?' + urlParams.toString())
          }
          document.title = system.systemName
          state.openWith({updatedUrlParams: urlParams, system, token, systemFetchError: false, fetchingSystem: false, autoLogin, newRegistrationForUsername, userEmail, firstName, lastName,...login})
        }
      })
  }


  useEffect(() => {
    const handleWindowScroll = () => {
      const classes = document.body.className.split(' ')
      if (!window.pageYOffset !== (classes.indexOf('scrolled') < 0)) {
        const className = classes.filter(_ => _ !== 'scrolled').join(' ')
        document.body.className = className + (window.pageYOffset ? ' scrolled' : '')
      }
    }
    window.addEventListener('scroll', handleWindowScroll)
    return window.removeEventListener('scroll', handleWindowScroll)
  },[])

  useEffect(() => {
    getSystem()
  },[])

  const {updatedUrlParams, system, systemFetchError, fetchingSystem, autoLogin, needsRegistration, needsJwtRegistration, newRegistration, newRegistrationForUsername, registrationValues, userEmail, firstName, lastName, token} = state.isOpen

  // In order to deal with AWS migrated ViVA customers acessing legacy PAS address
  // Redirect to the new URL with paths and updated params (dealing with payload for system discover)
  if(system && system.systemWebHooks && system.systemWebHooks.migratedUrl){
    window.location.href = system.systemWebHooks.migratedUrl+location.pathname+location.search;
    // window.location.href = system.systemWebHooks.migratedUrl+location.pathname+'?'+updatedUrlParams.toString();
  }
  const Theme = useMemo(()=> {
    const customColors = system && system.systemWebHooks && system.systemWebHooks.customColors ? system.systemWebHooks.customColors : {
      main: '#008bd2',
    };

    return AppTheme(customColors)
  } ,[system])
  const user = userToggle.isOpen
  const assignState = (newValue) => state.openWith(_.assign(_,newValue))
  return (
    <Suspense fallback={null}>
      <I18NProvider defaultLocale={getInitLocale} system={system}>
        <IntlStartlibsProvider system={system}>
          <ThemeProvider theme={Theme}>
            <Router>
              <div className="App">
                <Notifications/>
                <PurviewPageLoader/>
                {
              (fetchingSystem && <LoadingUser/>) ||
              (systemFetchError && <SystemFetchError error={systemFetchError} retry={getSystem}/>) ||
              (needsRegistration && <RegistrationForm userToggle={userToggle} newRegistration={newRegistration} idToken={needsRegistration} registrationValues={registrationValues} system={system}/>) ||
              (needsJwtRegistration && <JwtRegistrationForm userToggle={userToggle} jwt={needsJwtRegistration.jwt} expired={needsJwtRegistration.validationFailed} system={system}/>) ||
              (newRegistrationForUsername &&
                <RegistrationForm userEmail={userEmail} userToggle={userToggle} userFirstName={firstName} userLastName={lastName} newRegistrationForUsername={newRegistrationForUsername} system={system}/>) ||
              (!needsRegistration &&
                <Switch>
                  {user.email && <Route path="/account">{() => <AccountSettings system={system} userToggle={userToggle}/>}</Route>}
                  <Route path="/activate-success">{() => <ActivationSuccess token={token} system={system}/>}</Route>
                  <Route path="/activate">{() => <Activate system={system}/>}</Route>
                  <Route path="/account/email-change/finish">{() => <EmailChangeFinish system={system}/>}</Route>
                  <Route path="/recovery">{() => <PasswordRecovery system={system}/>}</Route>
                  <Route path="/redefine-password">{() => <RedefinePassword system={system}/>}</Route>
                  <Route path="/reset">{() => <RedefinePassword system={system} autoLogin={autoLogin}/>}</Route>
                  <Route path="/2fa">{() => <LoginForm system={system} userToggle={userToggle}/>}</Route>
                  <Route path="/settings">{() => <LoginForm loginToSettings system={system} userToggle={userToggle}/>}</Route>
                  <Route path="/error/linkexpired">{() => <LinkExpiredError system={system}/>}</Route>
                  <Route path="/error/deleted-account">{() => <DeletedAccountError system={system}/>}</Route>
                  <Route path="/error/access-denied">{() => <AccessDenied system={system}/>}</Route>
                  <Route path="/error/url-access-denied">{() => <UrlAccessDenied system={system}/>}</Route>
                  <Route path="/error/activated">{() => <AlreadyActivated system={system}/>}</Route>
                  <Route path="/error/usernotfound">{() => <UserNotFound system={system}/>}</Route>
                  <Route path="/change-email-success">{() => <LoginForm fromActivation system={system} userToggle={userToggle}/>}</Route>
                  <Route path="/" exact key={location.state && location.state.loginKey}>{() =>
                    <LoginForm
                      userToggle={userToggle}
                      needsRegistration={(idToken,username) => assignState({needsRegistration: idToken, registrationValues: {username}})} system={system}
                    />
                  }</Route>
                  <Route path="/sso" exact key={location.state && location.state.loginKey}>{() => <SsoLogin
                    userToggle={userToggle}
                    needsRegistration={(idToken,username) => assignState({needsRegistration: idToken, registrationValues: {username}})} system={system}/>}</Route>
                  <Route path="/auth/:systemId" key={location.state && location.state.loginKey}>{() => <LoginForm
                    userToggle={userToggle}
                    needsRegistration={(idToken,username) => assignState({needsRegistration: idToken, registrationValues: {username}})} system={system}/>}</Route>
                  <Route path="/auth" key={location.state && location.state.loginKey}>{() => <LoginForm
                    userToggle={userToggle}
                    needsRegistration={(idToken,username) => assignState({needsRegistration: idToken, registrationValues: {username}})} system={system}/>}</Route>
                  <Route path="/login" key={location.state && location.state.loginKey}>{() => <LoginForm
                    userToggle={userToggle}
                    needsRegistration={(idToken,username) => assignState({needsRegistration: idToken, registrationValues: {username}})} system={system} autoLogin={autoLogin}/>}</Route>
                  <Redirect to={"/"+(system ? '?systemId='+system.systemId : '')}/>
                </Switch>)
            }

                <div style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100}}>
                  <Slot name="Popup"/>
                </div>
                <div style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 200}}>
                  <Slot name="Overlay"/>
                </div>
                <div style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 200}}>
                  <Slot name="Portal"/>
                  <Slot name="Dialog"/>
                  <Slot name="Confirm-Dialog"/>
                  <Slot name="Notification"/>
                  <Slot name="Notifications"/>
                </div>
              </div>
            </Router>
          </ThemeProvider>
        </IntlStartlibsProvider>
      </I18NProvider>
    </Suspense>
  )
}
/*class App extends React.Component {
  render() {
    const {location, user} = this.props
  }
}*/



const SystemFetchError = ({error, retry}) => <div>
  <FormattedMessage
    defaultMessage="An error has occurred <Button>Retry</Button>"
    description="System fetch error message"
    values={{
      Button: chunks => <Button onClick={retry}>{chunks}</Button>
    }}
  />
</div>


export default App
